import { useEffect } from 'react';
import { Form, Input, Select } from 'antd';
import clsx from 'clsx';
import { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { FormModal } from '@/components/form-modal/form-modal';
import { TimePicker } from '@/components/time-picker/time-picker';
import { useCreateSalesAgent, useUpdateSalesAgent } from '@/hooks/use-salespeople/use-salespeople';
import { useUser } from '@/hooks/use-user/use-user';
import {
  SalesAgent,
  SalesAgentPayload,
} from '@/services/outbound-calls-service/salespeople.service.types';
import { openNotification } from '@/utils/notification/notification';
import { DEFAULT_PHONE_PREFIX, phoneRule } from '@/utils/phone';
import { formFieldErrors, fromTimeValue, toTimeValue } from '../outbound-calls.utils';
import { WeekdayChips } from '../weekday-chips';
import styles from '../outbound-calls.module.scss';

// No users list in /api/v2, so an account can be the signed-in user's or the one already
// linked, and nobody else's.
type AccountChoice = 'none' | 'mine' | 'linked';

type SalesAgentValues = {
  name: string;
  phone: string;
  work_days: number[];
  work_start: Dayjs | null;
  work_end: Dayjs | null;
  account: AccountChoice;
};

const FIELDS = ['name', 'phone', 'work_days', 'work_start', 'work_end', 'account'] as const;

interface SalespersonFormModalProps {
  open: boolean;
  companyId: number;
  salesAgent?: SalesAgent;
  onClose: () => void;
}

export function SalespersonFormModal({
  open,
  companyId,
  salesAgent,
  onClose,
}: SalespersonFormModalProps) {
  const { t } = useTranslation(['outbound', 'common']);
  const [form] = Form.useForm<SalesAgentValues>();
  const { userData } = useUser();
  const { createSalesAgent, isPending: isCreating } = useCreateSalesAgent(companyId);
  const { updateSalesAgent, isPending: isUpdating } = useUpdateSalesAgent();

  // The API refuses an account from another company, so a platform admin is not offered theirs.
  const canLinkMine = (userData?.company?.id ?? userData?.company_id) === companyId;
  const linkedIsMine = !!salesAgent?.user_id && salesAgent.user_id === userData?.id;

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (!salesAgent) return;
    let account: AccountChoice = 'none';
    if (linkedIsMine) account = 'mine';
    else if (salesAgent.user_id) account = 'linked';
    form.setFieldsValue({
      name: salesAgent.name,
      phone: salesAgent.phone,
      work_days: salesAgent.work_days,
      work_start: toTimeValue(salesAgent.work_start),
      work_end: toTimeValue(salesAgent.work_end),
      account,
    });
  }, [open, salesAgent, linkedIsMine, form]);

  const accountOptions = [
    { value: 'none', label: t('outbound:salespeople.form.accountNone') },
    ...(canLinkMine || linkedIsMine
      ? [
          {
            value: 'mine',
            label: t('outbound:salespeople.form.accountMine', { email: userData?.email ?? '' }),
          },
        ]
      : []),
    ...(salesAgent?.user_id && !linkedIsMine
      ? [{ value: 'linked', label: salesAgent.user_email ?? String(salesAgent.user_id) }]
      : []),
  ];

  const userIdFor = (account: AccountChoice) => {
    if (account === 'mine') return userData?.id ?? null;
    if (account === 'linked') return salesAgent?.user_id ?? null;
    return null;
  };

  const handleSubmit = async (values: SalesAgentValues) => {
    const payload: SalesAgentPayload = {
      name: values.name.trim(),
      phone: values.phone.trim(),
      work_days: values.work_days ?? [],
      work_start: fromTimeValue(values.work_start),
      work_end: fromTimeValue(values.work_end),
      user_id: userIdFor(values.account),
    };
    try {
      if (salesAgent) await updateSalesAgent({ id: salesAgent.id, payload });
      else await createSalesAgent(payload);
      onClose();
    } catch (error) {
      const fields = formFieldErrors(error, FIELDS, {
        aliases: { user_id: 'account' },
        taken: { phone: t('outbound:salespeople.form.phoneTaken') },
      });
      if (fields.length) {
        form.setFields(fields);
        return;
      }
      openNotification({ type: 'error', message: t('outbound:salespeople.form.saveFailed') });
    }
  };

  return (
    <FormModal
      open={open}
      title={
        salesAgent
          ? t('outbound:salespeople.form.editTitle')
          : t('outbound:salespeople.form.createTitle')
      }
      subtitle={t('outbound:salespeople.form.subtitle')}
      submitLabel={salesAgent ? t('common:actions.save') : t('common:actions.create')}
      onSubmit={() => form.submit()}
      onCancel={onClose}
      isLoading={isCreating || isUpdating}
      width={560}
    >
      <Form<SalesAgentValues>
        form={form}
        name="salesperson"
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          phone: DEFAULT_PHONE_PREFIX,
          work_days: [],
          work_start: null,
          work_end: null,
          account: 'none',
        }}
      >
        <Form.Item
          label={t('outbound:salespeople.form.name')}
          name="name"
          rules={[
            {
              required: true,
              whitespace: true,
              message: t('outbound:salespeople.form.nameRequired'),
            },
          ]}
        >
          <Input maxLength={255} />
        </Form.Item>
        <Form.Item
          label={t('outbound:salespeople.form.phone')}
          name="phone"
          rules={[
            { required: true, message: t('outbound:salespeople.form.phoneRequired') },
            phoneRule(t('outbound:salespeople.form.phoneInvalid')),
          ]}
        >
          <Input placeholder="+48 123 456 789" />
        </Form.Item>

        <Form.Item
          label={t('outbound:salespeople.form.workDays')}
          name="work_days"
          extra={t('outbound:salespeople.form.workDaysHelp')}
        >
          <WeekdayChips label={t('outbound:salespeople.form.workDays')} />
        </Form.Item>

        <Form.Item
          label={t('outbound:salespeople.form.workHours')}
          extra={t('outbound:salespeople.form.workHoursHelp')}
        >
          <div className={clsx(styles.fieldRow, styles.subfields)}>
            <Form.Item className={styles.grow} name="work_start">
              <TimePicker aria-label={t('outbound:salespeople.form.workStart')} />
            </Form.Item>
            <Form.Item
              className={styles.grow}
              name="work_end"
              dependencies={['work_start']}
              rules={[
                ({ getFieldValue }) => ({
                  validator: (_, value: Dayjs | null) => {
                    const start: Dayjs | null = getFieldValue('work_start');
                    if (!value && !start) return Promise.resolve();
                    if (!value || !start) {
                      return Promise.reject(new Error(t('outbound:fieldError.bothHours')));
                    }
                    return value.isAfter(start)
                      ? Promise.resolve()
                      : Promise.reject(new Error(t('outbound:fieldError.hoursOrder')));
                  },
                }),
              ]}
            >
              <TimePicker aria-label={t('outbound:salespeople.form.workEnd')} />
            </Form.Item>
          </div>
        </Form.Item>

        <Form.Item
          label={t('outbound:salespeople.form.account')}
          name="account"
          extra={t('outbound:salespeople.form.accountHelp')}
        >
          <Select options={accountOptions} />
        </Form.Item>
      </Form>
    </FormModal>
  );
}
